import React from 'react';

import { useIntl } from 'react-intl';
import { Select } from 'antd';
import type { Moment } from 'moment';

import Title from '@src/components/BoxContainer/components/Title';
import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import RelativeAbsolutePeriodFilter, {
  type RelativeAbsolutePeriodValue,
} from '@src/components/RelativeAbsolutePeriodFilter';
import { SCHEDULE_TYPES } from '@src/constants/commons';
import CostCenterFilterSelector from '@src/packages/costCenters/components/CostCenterFilterSelector';
import { DateUtil } from '@src/packages/utils/dates';
import { REMOTE_CONFIG_KEYS } from '@src/packages/utils/hooks/remoteConfigs/remoteConfigKeys';
import { useRemoteConfigAccess } from '@src/packages/utils/hooks/remoteConfigs/useRemoteConfigAccess';
import IntlMessages from '@src/util/IntlMessages';

import ScheduleSelector from '../../../SchedulesDashboard/components/ScheduleSelector';
import { dateTypes } from '../../../SchedulesDashboard/constants';
import {
  buildCheckpointRoutesFilterSetValueProps,
  CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS,
  checkpointRoutesFiltersToPeriodValue,
  filterValuesToCheckpointRoutesFiltersUi,
  periodValueToCheckpointRoutesFilterProps,
  SCHEDULES_REPORT_MAX_SPAN,
  type CheckpointRoutesChartFilters,
} from '../../checkpointRoutesFilterConfig';
import type { ScheduleDashboardDateType } from '../../types';

const { Option } = Select;

interface CheckpointRoutesDashboardFiltersProps {
  handleFilters: (props: SetValueProps | SetValueProps[]) => void;
  filters: FilterValues;
  chartFilters: CheckpointRoutesChartFilters;
  divisionIds: string[];
  disabledDate: (date: Moment) => boolean;
  onSchedulesChange: (schedules: CheckpointRoutesChartFilters['schedules']) => void;
  onScheduleSelectorLoading: (loading: boolean) => void;
}

const CheckpointRoutesDashboardFilters: React.FC<CheckpointRoutesDashboardFiltersProps> = ({
  handleFilters,
  filters,
  chartFilters,
  divisionIds,
  onSchedulesChange,
  onScheduleSelectorLoading,
}) => {
  const intl = useIntl();
  const { isAllowed: costCentersAllowed } = useRemoteConfigAccess(REMOTE_CONFIG_KEYS.COST_CENTERS);
  const uiFilters = filterValuesToCheckpointRoutesFiltersUi(filters, chartFilters);
  const { groupBy, schedules, costCenterIds } = uiFilters;
  const periodValue = checkpointRoutesFiltersToPeriodValue(uiFilters);

  const handlePeriodChange = (period: RelativeAbsolutePeriodValue) => {
    handleFilters(periodValueToCheckpointRoutesFilterProps(period, intl));
  };

  const handleGroupByChange = (value: ScheduleDashboardDateType) => {
    handleFilters(
      buildCheckpointRoutesFilterSetValueProps(
        CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.GROUP_BY,
        value,
        intl,
      ),
    );
  };

  const handleCostCentersChange = (next: string[]) => {
    handleFilters(
      buildCheckpointRoutesFilterSetValueProps(
        CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.COST_CENTERS,
        next,
        intl,
      ),
    );
  };

  return (
    <div className="gx-flex-column gx-guarnic-gap-3">
      <RelativeAbsolutePeriodFilter
        historicalOnly
        value={periodValue}
        onChange={handlePeriodChange}
        label={<IntlMessages id="dashboards.schedules.dateRange" />}
        allowClear={false}
        defaultAbsoluteRange={() => [...chartFilters.dateRange]}
        maxSpan={SCHEDULES_REPORT_MAX_SPAN}
        showPresets
        timeZone={DateUtil.getDefaultTimeZone()}
      />
      <KeyValueWidget
        label={<Title.Filter value={<IntlMessages id="dashboards.schedules.groupResultsBy" />} />}
        value={
          <Select<ScheduleDashboardDateType>
            id="groupBy"
            onChange={handleGroupByChange}
            value={groupBy}
            dropdownMatchSelectWidth={false}
          >
            {dateTypes.map(d => (
              <Option key={d} value={d}>
                <IntlMessages id={`dateTypes.${d}`} values={{ amount: 1 }} />
              </Option>
            ))}
          </Select>
        }
      />
      <KeyValueWidget
        label={
          <Title.Filter
            value={<IntlMessages id="dashboards.chart.form.scheduleCompliance.groupBy.routes" />}
          />
        }
        value={
          <ScheduleSelector
            selectedSchedules={schedules}
            setSelected={onSchedulesChange}
            setLoading={onScheduleSelectorLoading}
            divisionIds={divisionIds}
            type={SCHEDULE_TYPES.checkpoint_route}
          />
        }
      />
      {costCentersAllowed ? (
        <KeyValueWidget
          label={<Title.Filter value={<IntlMessages id="costCenters.filter.label" />} />}
          value={
            <CostCenterFilterSelector
              value={costCenterIds}
              onChange={handleCostCentersChange}
              forceVisible
            />
          }
        />
      ) : null}
    </div>
  );
};

export default CheckpointRoutesDashboardFilters;
